/**
 * Provider stack, in the only order that works:
 *
 *   QueryClientProvider → SessionProvider → ThemeProvider → ToastHost → Router
 *
 * `SessionProvider` fetches `/me` through the api client, which needs no provider of
 * its own; it then pushes the CSRF token into that client. `ThemeProvider` owns the
 * themed `.bp` root every token resolves against, so it must wrap everything visible.
 * `ToastHost` sits inside the theme (its pill is themed) and outside the router (a
 * toast survives the navigation that triggered it).
 */
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { ToastHost } from "@/components/shell/ToastHost";
import { queryClient as defaultQueryClient } from "@/lib/queryClient";
import { SessionProvider } from "@/lib/session";
import { ThemeProvider, type Theme } from "@/lib/theme";
import { AppRouter } from "@/routes";
import type { Me } from "@hrms/shared";

export interface AppProps {
  /** Test seam: an isolated client per test. */
  client?: QueryClient;
  /** Test seam: seeds the session instead of fetching `/me`. */
  initialMe?: Me | null;
  /** Test seam: pins the theme. */
  initialTheme?: Theme;
}

export function App({ client = defaultQueryClient, initialMe, initialTheme }: AppProps = {}) {
  return (
    <QueryClientProvider client={client}>
      <SessionProvider initialMe={initialMe}>
        <ThemeProvider initialTheme={initialTheme}>
          <ToastHost>
            <AppRouter />
          </ToastHost>
        </ThemeProvider>
      </SessionProvider>
    </QueryClientProvider>
  );
}

export default App;
